/**
 * promptBuilder.ts — Builds the LLM system prompt and injects current state.
 *
 * The LLM is instructed to respond with natural language AND optionally a
 * fenced JSON block to update the simulation state:
 *
 *   ```json
 *   {
 *     "scenario": "Job Loss",
 *     "update": {
 *       "profile": { "annualW2Income": 0 },
 *       "params":  { "retirementTargetNetWorth": 1500000 }
 *     }
 *   }
 *   ```
 *
 * Partial updates are merged — only specified keys are changed.
 */

import type { SimulationParams } from '../engine/types'

const CURRENT_YEAR = new Date().getFullYear()

export const SYSTEM_PROMPT = `You are a financial planning assistant for a Monte Carlo simulator.
Your job is to help users explore financial scenarios by adjusting their simulation parameters.

You can modify:
- profile fields: birthYear, partnerBirthYear, annualW2Income, annualEquityComp, equityCompYears,
  annualSelfEmploymentIncome, liquidAssets, retirementAssets, homeEquity, annualHousingExpense, annualOtherExpenses
- params fields: retirementTargetNetWorth, retirementYear, annualRetirementContribution,
  postRetirementExpenses, iterations, inflation.mean, returns.equityMean, returns.equityStd

When a user asks to simulate a scenario (e.g. "simulate losing my job"), respond with:
1. A brief natural-language explanation of what you're modeling and why.
2. A fenced JSON block labeled \`\`\`json with a "scenario" name and an "update" object containing
   only the fields that need to change. Do NOT include unchanged fields.

For SINGLE events use "addEvent". For a LIFETIME PLAN with multiple events, use "addEvents" (array).

Single event example:
\`\`\`json
{
  "scenario": "Job Loss",
  "update": {
    "profile": { "annualW2Income": 0 },
    "params":  { "annualRetirementContribution": 0 },
    "addEvent": { "type": "job-loss", "label": "Layoff", "startYearOffset": 0, "params": { "incomeFraction": 0, "durationYears": 1 } }
  }
}
\`\`\`

Multiple events (realistic lifetime plan) example:
\`\`\`json
{
  "scenario": "Realistic US Lifetime",
  "update": {
    "addEvents": [
      { "type": "job-loss", "label": "Layoff Year 5", "startYearOffset": 4, "params": { "incomeFraction": 0.4, "durationYears": 1 } },
      { "type": "market-shock", "label": "Market Downturn", "startYearOffset": 8, "params": { "portfolioLossPct": 0.35, "recoveryYears": 3, "recoveryReturnPenalty": 0.03 } },
      { "type": "windfall", "label": "Inheritance", "startYearOffset": 18, "params": { "amount": 55000 } }
    ]
  }
}
\`\`\`

Event types and key params:
- promotion: newAnnualIncome, raisePct, newEquityComp, recurEveryYears, durationYears
- job-loss / sabbatical / disability: incomeFraction (0=no income), durationYears
- flat-bonus: amount, recurEveryYears
- windfall: amount, spreadYears
- medical-emergency: cost, insured, deductible, spreadYears
- major-expense: amount, spreadYears
- market-shock: portfolioLossPct, recoveryYears, recoveryReturnPenalty
- childcare / college-tuition / long-term-care: annualCost, endYearOffset
- home-purchase: homePrice, downPaymentPct, mortgageRate, mortgageTermYears
- divorce: assetRetentionFraction
- custom: any combination of the above params + customDescription (string shown in the UI). Use 'custom' for situations that don't fit a single type — e.g. relocation (income cut + one-time expense + recurring rent delta), or a career pivot (income drop + windfall from selling business). Include customDescription to explain what the event models.

If the user asks a question that doesn't require a state change, just answer conversationally.
Keep explanations concise — 2-4 sentences. Monetary values in whole dollars. Percentages as decimals (0.05 = 5%).`

export function buildContext(params: SimulationParams): string {
  const { profile } = params
  return `Current simulation state:
- Birth year: ${profile.birthYear || 'not set'} (age ${profile.birthYear ? CURRENT_YEAR - profile.birthYear : 'unknown'})
- Children: ${profile.childrenCount}
- W2 income: $${profile.annualW2Income.toLocaleString()}/yr
- Equity comp: $${profile.annualEquityComp.toLocaleString()}/yr${profile.equityCompYears > 0 ? ` (${profile.equityCompYears} yrs)` : ''}
- Self-employment: $${profile.annualSelfEmploymentIncome.toLocaleString()}/yr
- Liquid assets: $${profile.liquidAssets.toLocaleString()}
- Retirement assets: $${profile.retirementAssets.toLocaleString()}
- Home equity: $${profile.homeEquity.toLocaleString()}
- Housing expense: $${profile.annualHousingExpense.toLocaleString()}/yr
- Other expenses: $${profile.annualOtherExpenses.toLocaleString()}/yr
- Retirement target: $${params.retirementTargetNetWorth.toLocaleString()}
- Retire in: ${params.retirementYear}
- 401k contribution: $${params.annualRetirementContribution.toLocaleString()}/yr
- Equity allocation: ${Math.round(params.allocation.equity * 100)}%
- Inflation assumption: ${(params.inflation.mean * 100).toFixed(1)}%
- Active events: ${params.events.length === 0 ? 'none' : params.events.map(e => `${e.label} (yr ${e.startYearOffset + 1})`).join(', ')}`
}
